import {AlertFallback} from '@rdcomponents/alert-fallback/alert-fallback';
import {PageHeaderTitle} from '@rdcomponents/page-header-title/page-header-title';
import {dateRangeToWorksheetName} from '@rdcomponents/request-report-base/request-report-base-helper';
import {UsetifulNameProductEnum} from '@rdcomponents/usetiful/usetiful-const';
import {Radio, Space, Table, TableColumnsType} from 'antd';
import {SorterResult} from 'antd/lib/table/interface';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {PageHeader} from '../../../../../layout/page-header/page-header';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../provider/locale/localization';
import {MainFilterContext} from '../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../provider/main-filter/main-filter-type';
import {AnalyticsTarget, track} from '../../../../../service/analytics/analytics';
import {fetchReviewCompanyGroups} from '../../../../../service/reivew-analysis/review-companies/review-companies-api';
import {isReviewAnalysisCompanySortKey} from '../../../../../service/reivew-analysis/review-companies/review-companies-helper';
import {useReviewCompanyGroups} from '../../../../../service/reivew-analysis/review-companies/review-companies-hook';
import {
    ReviewAnalysisCompaniesSortType,
    ReviewsAnalysisCompaniesQueryType,
    ReviewsAnalysisCompanyGroupType,
} from '../../../../../service/reivew-analysis/review-companies/review-companies-type';
import {ReviewsAndAnswersStatisticsUnitMeasurementEnum} from '../../../../../service/reviews-and-answers-statistics/reviews-and-answers-statistics';
import {TimeSizeEnum} from '../../../../../util/format';
import {useFormat} from '../../../../../util/format-hook/format-hook';
import {defaultPageSize} from '../../../../../util/pagination-hook/pagination-const';
import {useSavedTableSort} from '../../../../../util/save-sort-table-state/save-sort-table-hook';
import {saveNewSortTableState} from '../../../../../util/save-sort-table-state/save-sort-table-state';
import {ActiveFilter} from '../../components/active-filter/active-filter';
import {DrawerFilter} from '../../components/drawer-filter/drawer-filter';
import {RequestReport} from '../../components/request-report/request-report';
import {useReviewsAnalysisFilter} from '../../reviews-analysis-hook';

import {formatInitialStateSort, getCompanyGroupsPageTableColumns, serializeSortOrder} from './companies-page-helper';
import {ReviewCompanyGroupsTableDataType} from './companies-page-type';
import * as styles from './companies-page.scss';

type PropsType = {
    mode: 'companies' | 'companyGroups';
    setMode: (mode: 'companies' | 'companyGroups') => void;
};

// eslint-disable-next-line max-statements
export function ReviewAnalysisCompanyGroupsTable(props: PropsType): JSX.Element {
    const {mode, setMode} = props;
    const {getLocalizedString} = useLocale();
    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const {routerLocation, savedSortState} = useSavedTableSort();

    const {singleFilter} = useReviewsAnalysisFilter();
    const [pageSize, setPageSize] = useState<number>(defaultPageSize);
    const [sort, setSort] = useState<ReviewAnalysisCompaniesSortType | null>(formatInitialStateSort(savedSortState));

    const {fetch, result, isInProgress, processError} = useReviewCompanyGroups();
    const page: number = result?.page || 1;
    const total: number = result?.count || 0;

    const {getFormattedNumber} = useFormat();

    useEffect(() => {
        fetch({page: 1, pageSize, sort, ...singleFilter}, mainFilterKey);
    }, [mainFilterKey, fetch, pageSize, sort, singleFilter]);

    function fetchPage(pageInner: number) {
        fetch({page: pageInner, pageSize, sort, ...singleFilter}, mainFilterKey);
    }

    const getDataSource = useCallback((array: Array<ReviewsAnalysisCompanyGroupType>, isForExport?: boolean) => {
        return array.map<ReviewCompanyGroupsTableDataType>(
            (dataItem: ReviewsAnalysisCompanyGroupType, index): ReviewCompanyGroupsTableDataType => ({
                ...dataItem,
                key: `${dataItem.companyGroups[0]} ${index} ${isForExport && 'export'}`,
            })
        );
    }, []);

    const dataSource: Array<ReviewCompanyGroupsTableDataType> = useMemo<Array<ReviewCompanyGroupsTableDataType>>(
        (): Array<ReviewCompanyGroupsTableDataType> => getDataSource(result?.results || []),
        [getDataSource, result?.results]
    );

    function onPaginationChange(pageInner: number, pageSizeInner?: number | void): void {
        if (pageSizeInner && pageSize !== pageSizeInner) {
            setPageSize(pageSizeInner);
            return;
        }

        fetchPage(pageInner);

        if (result?.page !== pageInner) {
            track(AnalyticsTarget.PersonalCabinet.Pagination, pageInner);
        }
    }

    function addDefaultSortOrder(
        columns: TableColumnsType<ReviewCompanyGroupsTableDataType>,
        defaultSortColumn: ReviewAnalysisCompaniesSortType | null
    ): TableColumnsType<ReviewCompanyGroupsTableDataType> {
        if (!defaultSortColumn) {
            return columns;
        }

        const [dataIndex, sortOrder] = defaultSortColumn?.split('.') ?? [];

        if (!dataIndex || !sortOrder) {
            return columns;
        }

        const normalizedSortOrder = sortOrder === 'asc' ? 'ascend' : 'descend';

        return columns.map((column) => {
            if ('dataIndex' in column && column.dataIndex === dataIndex && column.sorter) {
                return {
                    ...column,
                    defaultSortOrder: normalizedSortOrder,
                };
            }

            return column;
        });
    }

    function onSortingChange(sorter: SorterResult<ReviewCompanyGroupsTableDataType>) {
        const sortKey = String(sorter.field);
        const serializedOrder = serializeSortOrder(sorter.order);

        if (serializedOrder && isReviewAnalysisCompanySortKey(sortKey)) {
            setSort(`${sortKey}.${serializedOrder}`);
            saveNewSortTableState(routerLocation, sorter);
        } else {
            setSort(null);
            saveNewSortTableState(routerLocation, sorter);
        }
    }

    function formatDataForXls(data: Array<ReviewsAnalysisCompanyGroupType>) {
        return data.map((company) => ({
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__COMPANY_GROUP')]: company.companyGroups.join('\n'),
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__RATE')]: company.avgRating.toFixed(1),
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__REVIEW_TOTAL')]: getFormattedNumber(company.total),
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__WITH_MESSAGE_PERCENTAGE')]: `${getFormattedNumber(
                company.replied
            )} (${company.total > 0 ? getFormattedNumber(company.replied / company.total, {style: 'percent'}) : '0%'})`,
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__REPLY_DELAY')]: company.avgResponseSpeed
                ? getFormattedNumber(company.avgResponseSpeed, {style: 'unit', unit: TimeSizeEnum.minute})
                : '—',
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__POSITIVE'
            )}`]: company.sentiment.positive,
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__NEUTRAL'
            )}`]: company.sentiment.neutral,
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__NEGATIVE'
            )}`]: company.sentiment.negative,
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__UNSPECIFIED'
            )}`]: company.sentiment.unspecified,
        }));
    }

    useEffect(() => {
        fetch({page: 1, pageSize, sort, ...singleFilter}, mainFilterKey);
    }, [mainFilterKey, fetch, pageSize, sort, singleFilter]);

    return (
        <>
            <PageHeader>
                <div className={styles.ReviewGeneratorCompaniesPage_titleRightColumn}>
                    <PageHeaderTitle
                        productName={UsetifulNameProductEnum.REVIEWS_ANALYSIS__COMPANIES}
                        title="CATEGORY_NAME__REVIEWS_ANALYSIS__COMPANIES"
                    />

                    <Radio.Group
                        buttonStyle="solid"
                        onChange={(event) => {
                            setMode(event.target.value);
                        }}
                        optionType="button"
                        options={[
                            {
                                value: 'companies',
                                label: <Locale stringKey="CATEGORY_NAME__REVIEWS_ANALYSIS__COMPANIES" />,
                            },
                            {
                                value: 'companyGroups',
                                label: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__COMPANY_GROUPS" />,
                            },
                        ]}
                        value={mode}
                    />
                </div>

                <Space>
                    <RequestReport<
                        ReviewCompanyGroupsTableDataType,
                        ReviewsAnalysisCompanyGroupType,
                        ReviewsAnalysisCompaniesQueryType
                    >
                        apiHandler={fetchReviewCompanyGroups}
                        columns={getCompanyGroupsPageTableColumns({
                            timeUnit: singleFilter.timeFormat || ReviewsAndAnswersStatisticsUnitMeasurementEnum.Minutes,
                        })}
                        defaultFilename={getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__COMPANY_GROUPS')}
                        filters={{...singleFilter, sort}}
                        formatDataForXls={formatDataForXls}
                        getDataSource={getDataSource}
                        title={<Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__COMPANY_GROUPS" />}
                        xlsxWorksheetName={dateRangeToWorksheetName({
                            rawStartDate: singleFilter.startDate,
                            rawEndDate: singleFilter.endDate,
                        })}
                    />
                    <DrawerFilter />
                </Space>
            </PageHeader>

            <ActiveFilter
                isCompareRangeAllowed={false}
                isCompetitorAllowed={false}
                isPreview={false}
                isWithTextOnly={false}
            />

            {processError && <AlertFallback />}

            <Table<ReviewCompanyGroupsTableDataType>
                columns={addDefaultSortOrder(
                    getCompanyGroupsPageTableColumns({
                        timeUnit: singleFilter.timeFormat || ReviewsAndAnswersStatisticsUnitMeasurementEnum.Minutes,
                    }),
                    sort
                )}
                dataSource={dataSource}
                loading={isInProgress}
                onChange={(_pagination, _filter, sorter): void => {
                    if (!Array.isArray(sorter) && sorter.field) {
                        onSortingChange(sorter);
                    }
                }}
                pagination={{
                    total,
                    pageSize,
                    current: page,
                    size: 'default',
                    showSizeChanger: true,
                    onChange: onPaginationChange,
                }}
                scroll={{x: true}}
                showSorterTooltip={false}
                size="middle"
            />
        </>
    );
}
